import type { RfpCoverage } from "@platform/plan-grid-core";

/**
 * Blanks the Voluntary Life "Benefits Included" toggles on rate-only plans.
 *
 * `buildRowData` seeds every empty cell with its benefit definition's
 * `defaultValue`, and both `spouseBenefitsIncluded` and `childBenefitsIncluded`
 * default to "yes". A rate-only plan — a real, persisted column built from a
 * renewal/rate document — keeps its residual rate/structural fields (age
 * reduction schedules, riders, commission types) but carries none of the
 * spouse/child benefit-detail fields a real plan has. With no stored value for
 * the toggle, the "yes" default seeds, so the plan reads as "spouse/child
 * benefits included" when nothing was extracted (CD-3082).
 *
 * A toggle is answered only when the plan holds a real value for at least one of
 * the benefit-detail fields it governs — the `refreshesCells` the definition
 * already declares (e.g. `benefitDescriptionSpouseType`). A real plan whose
 * spouse benefits are not included still carries those fields (set to
 * "notIncluded"), so it keeps its "No"; a rate-only plan has none of them, so
 * its toggle blanks.
 *
 * Only the seeded default is blanked: if the plan stores a real value for the
 * toggle itself — which an analyst answering the toggle writes before any detail
 * field exists (`onCellValueChanged` persists `benefits[benefitKey]`) — that
 * explicit answer survives, so the next coverage update doesn't wipe it.
 * "Real value" mirrors the row builder's own empty-check: a key stored as `null`
 * or `""` is replaced by the seeded default downstream, so it counts as unset
 * here too and the cell is still blanked.
 *
 * Build-only — injected through `PlanDesigner`'s `transformRowData` seam, so the
 * seeding stays as-is for every other surface and field.
 */

const BENEFITS_INCLUDED_TOGGLE_KEYS: ReadonlySet<string> = new Set([
  "spouseBenefitsIncluded",
  "childBenefitsIncluded",
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// Mirrors buildRowData's getValueWithDefault: a value only counts as stored (and
// so is used instead of the seeded default) when it is not undefined/null/"".
const hasStoredValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== "";

/** The benefits record each plan carries, keyed by plan id. */
const benefitsByPlanId = (
  coverage: RfpCoverage | undefined,
): Map<string, Record<string, unknown>> =>
  new Map(
    (coverage?.benefitPlans ?? []).flatMap((plan) =>
      plan.id
        ? [[plan.id, (plan.benefits ?? {}) as Record<string, unknown>] as const]
        : [],
    ),
  );

type ToggleRow = Record<string, unknown> & {
  benefitKey: string;
  benefit: { refreshesCells?: readonly string[] };
};

const isBenefitsIncludedToggleRow = (row: unknown): row is ToggleRow =>
  isRecord(row) &&
  typeof row["benefitKey"] === "string" &&
  BENEFITS_INCLUDED_TOGGLE_KEYS.has(row["benefitKey"]) &&
  isRecord(row["benefit"]);

const blankTogglesWithoutDetail = (
  row: ToggleRow,
  benefitsByPlan: Map<string, Record<string, unknown>>,
): ToggleRow => {
  const governedKeys = row.benefit.refreshesCells ?? [];
  if (governedKeys.length === 0) return row;

  // A plan is treated as rate-only — and its toggle blanked — only when all
  // three hold: it carries some benefit data (excludes brand-new blank plans,
  // which keep the seeded default), it has no spouse/child detail, and it has no
  // explicit answer stored for the toggle itself.
  const planHasBenefitData = (benefits: Record<string, unknown>): boolean =>
    Object.keys(benefits).length > 0;
  const planHasNoSpouseChildDetail = (
    benefits: Record<string, unknown>,
  ): boolean => !governedKeys.some((key) => hasStoredValue(benefits[key]));
  const planHasNoExplicitAnswer = (
    benefits: Record<string, unknown>,
  ): boolean => !hasStoredValue(benefits[row.benefitKey]);

  const blanked = Object.fromEntries(
    [...benefitsByPlan]
      .filter(
        ([planId, benefits]) =>
          planId in row &&
          planHasBenefitData(benefits) &&
          planHasNoSpouseChildDetail(benefits) &&
          planHasNoExplicitAnswer(benefits),
      )
      .map(([planId]) => [planId, ""]),
  );

  return Object.keys(blanked).length > 0 ? { ...row, ...blanked } : row;
};

/**
 * Builds a `transformRowData` callback that blanks the Voluntary Life "Benefits
 * Included" toggles on rate-only plans. Compose it after any other row
 * transform so it observes the final row set.
 *
 * @param coverage - The coverage handed to `PlanEditor`
 */
export const buildSuppressRateOnlyBenefitsIncluded = (
  coverage: RfpCoverage | undefined,
): ((rows: unknown[]) => unknown[]) => {
  const benefitsByPlan = benefitsByPlanId(coverage);

  return (rows: unknown[]): unknown[] => {
    if (benefitsByPlan.size === 0) return rows;

    return rows.map((row) =>
      isBenefitsIncludedToggleRow(row)
        ? blankTogglesWithoutDetail(row, benefitsByPlan)
        : row,
    );
  };
};
